import { Stack } from '@mui/material';
import {
  Area,
  ProjectArea_Input,
  SaveProject_Input
} from 'generated/graphql';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { FormBaseLayout } from 'modules/components/layer10';
import {
  GeneralProjectInformationFields,
  ProjectInstallationsFields,
  ProjectRoomCountFields
} from 'modules/components/layer11';
import { useCallback } from 'react';
import * as ds from './dataService';



type IFieldNames<T extends object> = keyof T;

const __stepNumber2Fields: Record<number, IFieldNames<ds.IFormState>[]> = {
  1: [
    'projectName',
    'actualProjectStartDate',
    'estimatedProjectStartDate',
    'projectStreetOne',
    'projectStreetTwo',
    'projectCity',
    'projectState',
    'projectZipCode',
    'ignoreAreaSteps'
  ],
  2: ['area2NumberOfRooms'],
  3: ['namedAreasInProject']
};


export type SaveProjectFormProps = {
  id?: string;
  handleClose: () => void;
};
/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
export const FC = (props: SaveProjectFormProps) => {
  const { form } = ds.useQuery(props.id);

  const [
    fnCreateProject,
    createTuple
  ] = ds.useSaveProject();



  const handleSubmit = useCallback(async () => {
    if (form.formState.activeStep === 1 && form.formState.ignoreAreaSteps === 'true') {
      const input: SaveProject_Input = {
        id: props.id,
        areasToCreate: [],
        name: form.formState.projectName,
        startDate: DateTime.fromFormat(props.id
          ? form.formState.actualProjectStartDate
          : form.formState.estimatedProjectStartDate, 'yyyy-LL-dd'),
        location: {
          id: form.formState.locationAddressId,
          city: form.formState.projectCity,
          postalCode: form.formState.projectZipCode,
          streetOne: form.formState.projectStreetOne,
          streetTwo: form.formState.projectStreetTwo,
          stateId: form.formState.stateAbbreviation2Id[form.formState.projectState.toUpperCase()]
        }
      };

      await fnCreateProject(input);
      props.handleClose();
    }

    // if formstate === 1
    if (form.formState.activeStep === 2 && form.formState.area2NumberOfRooms) {
      form.dispatch({
        type: 'UPDATE',
        payload: {
          property: 'namedAreasInProject',
          value: __namedRoomsToGenerate(form.formState.area2NumberOfRooms)
        }
      });
    }

    if (form.formState.activeStep === 3) {
      const areasToGenerate = form.formState.namedAreasInProject.map((node): ProjectArea_Input => ({
        areaId: node.areaId,
        areaName: node.displayName
      }));

      const input: SaveProject_Input = {
        id: props.id,
        areasToCreate: areasToGenerate,
        name: form.formState.projectName,
        startDate: DateTime.fromFormat(props.id
          ? form.formState.actualProjectStartDate
          : form.formState.estimatedProjectStartDate, 'yyyy-LL-dd'),
        location: {
          id: form.formState.locationAddressId,
          city: form.formState.projectCity,
          postalCode: form.formState.projectZipCode,
          streetOne: form.formState.projectStreetOne,
          streetTwo: form.formState.projectStreetTwo,
          stateId: form.formState.stateAbbreviation2Id[form.formState.projectState]
        }
      };

      await fnCreateProject(input);
      props.handleClose();
    }

    // break up to seperate functions
    form.dispatch({
      type: 'UPDATE',
      payload: {
        property: 'activeStep',
        value: form.formState.activeStep += 1
      }
    });
  }, [
    fnCreateProject,
    form,
    props
  ]);

  const handlePrevious = useCallback(() => {
    form.dispatch({
      type: 'RESET'
    });

  }, [form]);


  return (
    <FormBaseLayout.FC
      handleCancel={() => {
        handlePrevious();
        props.handleClose();
      }}
      handleSubmit={() => {
        handleSubmit();
      }}
      cancelButtonText='Cancel'
      isLoading={createTuple.loading}
    >
      <Stack
        rowGap='1.25rem'
      >
        {form.formState.activeStep === 1 && (
          <GeneralProjectInformationFields.FC
            form={form}
            fieldNames={__stepNumber2Fields[1]}
            projectId={props.id}
          />
        )}

        {form.formState.activeStep === 2 && (
          <ProjectRoomCountFields.FC
            form={form}
            fieldName={_.first(__stepNumber2Fields[2]) ?? 'area2NumberOfRooms'}
          />
        )}

        {form.formState.activeStep === 3 && (
          <ProjectInstallationsFields.FC
            form={form}
            fieldNames={__stepNumber2Fields[3]}
          />
        )}
      </Stack>
    </FormBaseLayout.FC>
  );
};



function __namedRoomsToGenerate(rooms2Generate: Record<string, Area & { count: number }>): ds.INamedRoomArea[] {
  const rooms = _.keysIn(rooms2Generate);

  const areas = rooms.flatMap((room) => {
    const area = rooms2Generate[room];
    if (!area) {
      throw new Error('Room not found');
    }

    const numberOfRooms = area.count;
    const rooms = _.times(numberOfRooms, () => ({
      areaId: area.id,
      areaName: area.name,
      displayName: area.displayName
    }));
    return rooms;
  });

  return areas.map((node, i) => ({
    id: `${i}`,
    ...node
  }));
}


